// Core
import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { Button, FieldSelect, FieldText, FieldToggle, SectionCard } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Utils
import { isValidNif, isValidPhone } from "utils/validations";

function GeneralInfo({ data, onUpdate, isLoading }) {
  const [original, setOriginal] = useState(data);
  const [name, setName] = useState(data.name);
  const [email, setEmail] = useState(data.email);
  const [phone, setPhone] = useState(data.phone);
  const [nif, setNif] = useState(data.nif);
  const [status, setStatus] = useState(data.status);
  const [isHost, setIsHost] = useState(data.is_host ?? false);
  const [isPromoter, setIsPromoter] = useState(data.is_promoter ?? false);

  const { t } = useTranslation(["members"]);

  const canSave =
    [name, email, status].every(Boolean) &&
    !isLoading &&
    !(
      original.name === name &&
      original.email === email &&
      original.phone === phone &&
      original.nif === nif &&
      original.status === status &&
      original.is_host === isHost &&
      original.is_promoter === isPromoter
    ) &&
    isValidPhone(phone) &&
    isValidNif(nif);

  const onUpdateGeneralInfoClicked = async () => {
    if (canSave) {
      const newData = {
        email,
        name,
        phone,
        nif,
        status,
        is_host: isHost,
        is_promoter: isPromoter,
      };
      const success = await onUpdate(newData, t("single_member.general.updated_ok"));

      if (success) {
        setOriginal({ email, name, phone, nif, status, is_host: isHost, is_promoter: isPromoter });
      }
    }
  };

  const statusOptions = [
    { value: "ACTIVE", label: t("table.filters.status.active") },
    { value: "INACTIVE", label: t("table.filters.status.inactive") },
  ];

  return (
    <SectionCard
      id="general"
      icon="person"
      title={t("single_member.general.title")}
      action={
        <Button variant="ghost" size="sm" disabled={!canSave} onClick={onUpdateGeneralInfoClicked}>
          <Icon name="save" />
          {t("single_member.general.update_button")}
        </Button>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldText
          label={t("single_member.general.labels.company")}
          value={name}
          onChange={setName}
        />
        <FieldText
          label={t("single_member.general.labels.email")}
          type="email"
          value={email}
          onChange={setEmail}
        />
        <FieldText
          label={t("single_member.general.labels.phone")}
          value={phone}
          onChange={setPhone}
          error={!isValidPhone(phone)}
        />
        <FieldText
          label={t("single_member.general.labels.nif")}
          value={nif}
          onChange={setNif}
          error={!isValidNif(nif)}
        />
        <FieldSelect
          label={t("single_member.general.labels.status")}
          value={status}
          options={statusOptions}
          onChange={setStatus}
        />
      </div>
      <div className="rec-divider" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
        <FieldToggle
          label={t("single_member.general.labels.is_host")}
          desc={t("single_member.general.host_desc", {
            defaultValue: "Disponibiliza telhado/ponto de ligação para produção",
          })}
          value={isHost}
          onChange={setIsHost}
        />
        <FieldToggle
          label={t("single_member.general.labels.is_promoter")}
          desc={t("single_member.general.promoter_desc", {
            defaultValue: "Promove a comunidade de energia",
          })}
          value={isPromoter}
          onChange={setIsPromoter}
        />
      </div>
    </SectionCard>
  );
}

GeneralInfo.defaultProps = {
  isLoading: false,
};

GeneralInfo.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    nif: PropTypes.string,
    status: PropTypes.string,
    is_host: PropTypes.bool,
    is_promoter: PropTypes.bool,
    has_dashboard_access: PropTypes.bool,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default GeneralInfo;
